/**
 * loading.tsx — SINGLE SOURCE for every loading UI in the app.
 *
 * Route/guard fallbacks:  <PageSpinner fullScreen/> (pre-AppLayout / auth) · <ContentLoader/> (inside AppLayout content area)
 * Data-fetch states:      <Skeleton/> primitive + compositions (<DetailSkeleton/>, table/card skeletons)
 *
 * RULE: do NOT roll your own spinner/skeleton. Import from here so new loading
 * spots are consistent by default. One shimmer color = bg-background-muted.
 */

/** Base shimmer block. Size/shape via className. The ONLY shimmer style in the app. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-background-muted ${className}`} aria-hidden="true" />
}

function Spinner() {
  return (
    <div className="text-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" role="status" aria-label="Loading" />
      <p className="mt-2 text-sm text-subtle">Loading...</p>
    </div>
  )
}

/** Pre-AppLayout / auth / public fallback. fullScreen = whole viewport. */
export function PageSpinner({ fullScreen = false }: { fullScreen?: boolean }) {
  return <div className={`flex items-center justify-center ${fullScreen ? 'min-h-screen' : 'min-h-[60vh]'}`}><Spinner /></div>
}

/** The ONE in-AppLayout content fallback. Fixed height so permission-check and
 *  chunk-load phases render identically → no double-flash, no jump. */
export function ContentLoader() {
  return <div className="flex min-h-[60vh] items-center justify-center"><Spinner /></div>
}

/** Shared detail-page skeleton (replaces the 3 duplicated LoadingSkeletons). */
export function DetailSkeleton({ testId = 'detail-loading' }: { testId?: string }) {
  return (
    <div className="space-y-6" data-testid={testId}>
      <Skeleton className="h-24 w-full rounded-xl" />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-36 w-full rounded-xl" />
          <Skeleton className="h-52 w-full rounded-xl" />
        </div>
        <Skeleton className="h-64 w-full rounded-xl lg:sticky lg:top-4 lg:self-start" />
      </div>
    </div>
  )
}

/** Shared list-page skeleton (header bar + N table-style rows). */
export function ListSkeleton({ testId = 'list-loading', rows = 5 }: { testId?: string; rows?: number }) {
  return (
    <div className="space-y-3 p-6" data-testid={testId} aria-busy="true">
      <Skeleton className="h-10 w-full rounded-lg" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-lg" />
      ))}
    </div>
  )
}

export default PageSpinner
